"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuItem as SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import type { Role } from "../../api/custom";

export type Route = {
  id: string;
  title: string;
  icon?: React.ReactNode;
  link: string;
  isExternal?: boolean;
  isNew?: boolean;
  isDisabled?: boolean;
  requiredRole?: Role;
  subs?: {
    title: string;
    link: string;
    icon?: React.ReactNode;
    isExternal?: boolean;
    isNew?: boolean;
    isDisabled?: boolean;
  }[];
};

export default function DashboardNavigation({ routes }: { routes: Route[] }) {
  const { state, setOpen, isMobile } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [openCollapsible, setOpenCollapsible] = useState<string | null>(null);
  const routerState = useRouterState();
  const currentPath = routerState?.location?.pathname || "/";

  // Auto-open the section containing the active route on mount
  useEffect(() => {
    const activeRoute = routes.find((route) => {
      // Check if current path matches the route link
      if (route.link === currentPath) return true;
      // Check if current path matches any sub-route
      return route.subs?.some((sub) => sub.link === currentPath);
    });

    if (activeRoute && !isCollapsed) {
      setOpenCollapsible(activeRoute.id);
    }
  }, [currentPath, routes, isCollapsed]);

  const handleParentClick = (routeId: string, hasSubRoutes: boolean) => {
    if (isCollapsed && hasSubRoutes && !isMobile) {
      setOpen(true);
      setTimeout(() => {
        setOpenCollapsible(routeId);
      }, 100);
    }
  };

  return (
    <SidebarMenu>
      {routes.map((route) => {
        const isOpen = !isCollapsed && openCollapsible === route.id;
        const hasSubRoutes = !!route.subs?.length;

        return (
          <SidebarMenuItem key={route.id}>
            {hasSubRoutes ? (
              <Collapsible
                open={isOpen}
                onOpenChange={(open) =>
                  setOpenCollapsible(open ? route.id : null)
                }
                className="w-full"
              >
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    onClick={() => handleParentClick(route.id, hasSubRoutes)}
                    disabled={route.isDisabled}
                    className={cn(
                      "flex w-full items-center rounded-lg h-12 transition-colors group-data-[collapsible=icon]:!h-12 group-data-[collapsible=icon]:!w-full",
                      route.isDisabled
                        ? "opacity-50 cursor-not-allowed hover:cursor-not-allowed"
                        : isOpen
                        ? "bg-sidebar-muted text-foreground"
                        : "text-muted-foreground hover:bg-sidebar-muted hover:text-foreground",
                      isCollapsed ? "justify-center px-2" : "px-3 gap-3"
                    )}
                  >
                    <span
                      className={cn(
                        isCollapsed ? "[&>svg]:!size-5" : "[&>svg]:!size-5"
                      )}
                    >
                      {route.icon}
                    </span>
                    {!isCollapsed && (
                      <span className="flex-1 text-sm font-medium flex items-center gap-2">
                        {route.title}
                        {route.isNew && (
                          <span className="px-1.5 py-0 text-[10px] font-semibold rounded bg-green-500 text-white">
                            NEW
                          </span>
                        )}
                      </span>
                    )}
                    {!isCollapsed && hasSubRoutes && (
                      <span className="ml-auto">
                        {isOpen ? (
                          <ChevronUp className="size-5" />
                        ) : (
                          <ChevronDown className="size-5" />
                        )}
                      </span>
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                {!isCollapsed && (
                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-0 border-l-0 px-0 mt-1 space-y-0.5">
                      {route.subs?.map((subRoute) => (
                        <SidebarMenuSubItem
                          key={`${route.id}-${subRoute.title}`}
                        >
                          {subRoute.isExternal ? (
                            <SidebarMenuSubButton
                              asChild
                              className="!h-auto [&>span:last-child]:!whitespace-normal [&>span:last-child]:!overflow-visible [&>span:last-child]:!text-clip"
                            >
                              <a
                                href={subRoute.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                  "flex items-start gap-3 min-h-9 py-1.5 px-8 h-auto",
                                  subRoute.isDisabled &&
                                    "opacity-50 cursor-not-allowed hover:cursor-not-allowed pointer-events-none"
                                )}
                              >
                                <span className="[&>svg]:!size-4 shrink-0 mt-0.5">
                                  {subRoute.icon}
                                </span>
                                <span className="text-sm leading-snug flex-1 !whitespace-normal flex items-center gap-2">
                                  {subRoute.title}
                                  {subRoute.isNew && (
                                    <span className="px-1.5 py-0 text-[10px] font-semibold rounded bg-green-500 text-white">
                                      NEW
                                    </span>
                                  )}
                                  {subRoute.isExternal && (
                                    <ExternalLink className="size-4 text-muted-foreground shrink-0" />
                                  )}
                                </span>
                              </a>
                            </SidebarMenuSubButton>
                          ) : (
                            <RouterLink
                              to={subRoute.link}
                              disabled={subRoute.isDisabled}
                            >
                              {({ isActive }) => {
                                const isExactMatch = currentPath === subRoute.link;
                                const hasChildRoutes = route.subs?.some(
                                  (sub) => sub.link !== subRoute.link && sub.link.startsWith(subRoute.link)
                                );
                                const shouldBeActive = hasChildRoutes ? isExactMatch : isActive;
                                return (
                                  <SidebarMenuSubButton
                                    isActive={shouldBeActive}
                                    asChild
                                    className="!h-auto [&>span:last-child]:!whitespace-normal [&>span:last-child]:!overflow-visible [&>span:last-child]:!text-clip"
                                  >
                                    <div
                                      className={cn(
                                        "flex items-start gap-3 min-h-9 py-1.5 px-8 h-auto",
                                        subRoute.isDisabled &&
                                          "opacity-50 cursor-not-allowed hover:cursor-not-allowed pointer-events-none"
                                      )}
                                    >
                                      <span className="[&>svg]:!size-4 shrink-0 mt-0.5">
                                        {subRoute.icon}
                                      </span>
                                      <span className="text-sm leading-snug flex-1 !whitespace-normal flex items-center gap-2">
                                        {subRoute.title}
                                        {subRoute.isNew && (
                                          <span className="px-1.5 py-0 text-[10px] font-semibold rounded bg-green-500 text-white">
                                            NEW
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </SidebarMenuSubButton>
                                );
                              }}
                            </RouterLink>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </Collapsible>
            ) : (
              <SidebarMenuButton
                tooltip={route.title}
                asChild
                disabled={route.isDisabled}
              >
                <RouterLink
                  to={route.link}
                  disabled={route.isDisabled}
                  className={cn(
                    "flex items-center rounded-lg h-12 transition-colors text-muted-foreground hover:bg-sidebar-muted hover:text-foreground group-data-[collapsible=icon]:!h-12 group-data-[collapsible=icon]:!w-full",
                    route.isDisabled &&
                      "opacity-50 cursor-not-allowed hover:cursor-not-allowed",
                    isCollapsed
                      ? "justify-center px-2 [&>svg]:!size-5"
                      : "gap-3 px-3 [&>svg]:!size-5"
                  )}
                >
                  {route.icon}
                  {!isCollapsed && (
                    <span className="text-sm font-medium flex items-center gap-2">
                      {route.title}
                      {route.isNew && (
                        <span className="px-1.5 py-0 text-[10px] font-semibold rounded bg-green-500 text-white">
                          NEW
                        </span>
                      )}
                    </span>
                  )}
                </RouterLink>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}
